import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "What computer science can teach us about vaccine distribution",
  "description": "How would you rollout the covid vaccine for max fairness and speed?",
  "categories": "Thought Experiments, Computer Science",
  "published": "2021-01-15T00:00:00.000Z",
  "hero": "./img/screenshot-1610734119340.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Friend, how would you rollout the covid vaccine?`}</p>
    <p>{`You want to ensure equitable access, fairness, prioritize the vulnerable, immunize frontline workers, go as fast as supplies allow, and eventually get everyone immunized. If they want to.`}</p>
    <p>{`Fun thought experiment for you, huge problem for policy makers.`}</p>
    <p>{`While `}<a parentName="p" {...{
        "href": "https://www.cnbc.com/2021/01/07/israels-covid-vaccine-rollout-is-the-fastest-in-the-world.html"
      }}>{`Israel delivered the first dose to 1.5 million people`}</a>{` – 16% of the population, the US sits at `}<a parentName="p" {...{
        "href": "https://www.nytimes.com/interactive/2020/us/covid-19-vaccine-doses.html"
      }}>{`11 million`}</a>{` – 3% of the population. With some states going as fast as possible and others saying `}<em parentName="p">{`"We'll have a plan that ensures fairness in 5 weeks"`}</em>{`.`}</p>
    <p>{`Everyone is worried about jumping the line. Stealing doses from those more deserving. Yet up to 40% of those offered say no. Causing huge delays.`}</p>
    <p>{`🤦‍♂️`}</p>
    <p>{`How would you approach this?`}</p>
    <h2 {...{
      "id": "queueing-theory"
    }}>{`Queueing theory`}</h2>
    <p>{`You could try statistical regressions or plain old simulation, but really this is a problem for `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Queueing_theory"
      }}>{`queueing theory`}</a>{`. The most useful comp sci class I took in college.`}</p>
    <p>{`Not because I remember the formulae, because of the intuition. 😅`}</p>
    <p>{`Queueing theory talks about the behavior of queues. How do you design a system that processes every request in time? What if some requests fail? What if long waits lead to bailing? What if time to process a request varies? It's cool.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "500px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/b705e3a872836591b28f8cb52ba3dc84/0b533/A-basic-queue-modelhjhf1c.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "40.80717488789237%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'163\\'%20viewBox=\\'0%200%20400%20163\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M129%2015l19%201h19v75h-19c-16%200-19%200-19%202l59%201h59V61l2%205c13%2041%2072%2037%2080-5l1-6h29v4c0%202%200%203%202%203a1136%201136%200%200125-8l-26-8-1%204v3h-29l-1-7c-11-39-68-40-80-1l-2%206V13h-59c-52%200-59%200-59%202m41%2039v37h17V16h-17v38m20%200v37h16V16h-16v38m19%200v37h17V16h-17v38m20%200v37h15V16h-15v38m49-34c-22%207-34%2030-25%2050%209%2018%2031%2027%2050%2019%2012-4%2021-16%2023-28%205-25-22-48-48-41M30%2032l2%202c6%202%205%206%200%2018-5%2011-5%2011-3%2011s3-1%206-7l4-8%203%208c3%206%203%207%205%207%203%200%203%200-3-14l-7-16c-2-2-7-3-7-1m248%2024c0%2015%200%2016%202%2016s2-1%202-5c0-5%201-6%203-4%200%202%206%201%207%200%201-2%201-2%203%200s5%201%206-1l-1-2c-1%200-2-2-2-10%200-9%200-10-2-10s-2%201-2%209c-1%207-1%209-3%2010-6%205-8%202-9-10%200-8%200-9-2-9s-2%201-2%2016m-166-7v3H90c-20%200-23%200-23%202l23%201h22v4l1%203%2026-7c1-1-2-2-21-7l-6-2v3m-4%2068c3%209%203%2010%206%2010s3%200%204-6l2-7%202%207c1%206%201%206%204%206s4-1%204-4l2-10%202-6h-3l-2%201-2%207-2%205-1-7c-1-5-2-6-4-6l-2%201-2%207-2%205-1-6c-1-6-2-7-4-7h-3l2%2010m152-8c-4%204-2%208%205%209%204%202%205%205%201%204h-6c-2-1-2-1-2%201%200%204%2010%206%2014%202s2-8-5-10l-4-2c0-2%203-2%206-1s3%201%203-1c0-4-8-6-12-2m-98%201l-1%202-2%202%202%201%201%206c0%205%201%206%206%206l2-1-1-2c-4%200-4-8%200-9%203%200%203-3%200-3l-3-2c0-2-4-3-4%200m-26%203c-2%202-1%203%202%203%205-2%206%200%201%201-4%201-5%202-5%204%200%205%205%208%208%205h1l3%201c2%200%202%200%202-6%200-7-2-9-8-9l-4%201m46%207c0%206%200%207%202%207%201%200%202-1%202-5%201-8%205-8%205%200%200%205%200%205%203%205%202%200%202%200%202-6%200-8-1-9-8-9h-6v8m19-6l-1%205c0%206%201%208%206%208%204-1%204-1%203%201l-5%201c-2-1-3%200-3%201%200%203%205%204%2010%201%203-1%203-1%203-10v-9h-5l-8%202m78%200c-5%205-1%2013%207%2013%205%200%205%200%205-3%200-2%200-2-2-1s-7%201-7-1l5-1c5%200%205%200%205-2%200-4-3-7-7-7l-6%202m30%203l3%208%204%202c2%200%203-1%205-7l3-8h-3c-2%200-3%201-4%204l-1%205-2-5c-1-3-2-4-4-4h-3l2%205m26-3c-5%205-1%2013%206%2013%203%200%205-1%205-2%200-2%200-2-2-1-4%200-6-1-6-4%200-4%202-5%206-4%202%200%202%200%202-2-1-2-8-3-11%200m17%200c-3%202-3%209%200%2012%204%202%2011%201%2012-2%200-2%200-2-2-1s-7%201-7-1l5-1c6%200%207-2%203-6-2-3-7-4-11-1m-26%2016v3c0%203%200%203-3%203-5-1-8%202-8%207s2%207%209%207h7l-1-10c0-10%200-11-2-11l-2%201m-193%206l-1%202c0%202%200%202%202%201s6%200%206%201l-3%201c-6%200-8%203-6%207%201%202%202%202%207%202h7v-6c0-6-2-9-8-9l-4%201m32%200c-5%203-5%209-2%2012s12%203%2012%200c0-2-1-2-4-2l-5-1%205-1c5%200%205%200%205-2%200-5-7-9-11-6m16%200l-1%202c0%202%200%202%202%201s6%200%206%201l-3%201c-6%200-8%203-6%207%201%202%202%202%207%202h7v-6c0-6-2-9-8-9l-4%201m98%207c0%207%200%207%203%207%202%200%202%200%202-4%200-5%202-8%204-7l1%206c0%205%200%205%203%205%202%200%202%200%202-5%200-8-5-13-9-9h-1l-2-1c-3%200-3%200-3%208m21-6c-6%205-3%2013%205%2013%204%200%205-1%207-4%204-7-5-14-12-9m37%200c-3%202-4%207-1%2010%203%204%2013%204%2013%201%200-2%200-2-4-1l-5-1c-1-2-1-2%204-2s6%200%206-2c0-6-8-9-13-5\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b705e3a872836591b28f8cb52ba3dc84/ca0a1/A-basic-queue-modelhjhf1c.webp 223w", "/static/b705e3a872836591b28f8cb52ba3dc84/75680/A-basic-queue-modelhjhf1c.webp 445w", "/static/b705e3a872836591b28f8cb52ba3dc84/b0a15/A-basic-queue-modelhjhf1c.webp 500w"],
                "sizes": "(max-width: 500px) 100vw, 500px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b705e3a872836591b28f8cb52ba3dc84/e92b6/A-basic-queue-modelhjhf1c.png 223w", "/static/b705e3a872836591b28f8cb52ba3dc84/e66bf/A-basic-queue-modelhjhf1c.png 445w", "/static/b705e3a872836591b28f8cb52ba3dc84/0b533/A-basic-queue-modelhjhf1c.png 500w"],
                "sizes": "(max-width: 500px) 100vw, 500px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/b705e3a872836591b28f8cb52ba3dc84/0b533/A-basic-queue-modelhjhf1c.png",
                "alt": "A basic queue model",
                "title": "A basic queue model",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`A basic queue model`}</figcaption>{`
  `}</figure></p>
    <p>{`λ – request rate (typical time between requests)
μ – typical number of requests served per unit of time`}</p>
    <p>{`A basic `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/M/M/1_queue"
      }}>{`M/M/1 queue`}</a>{` – memoryless arrival, memoryless service time, 1 server – assumes requests don't affect each other. Complexity blows up fast when they do.`}</p>
    <h2 {...{
      "id": "queuing-theory-and-vaccines"
    }}>{`Queuing theory and vaccines`}</h2>
    <p>{`Let's assume we have infinite supply. You can buy or produce vaccines as fast as we can distribute them.`}</p>
    <p>{`USA has `}<a parentName="p" {...{
        "href": "https://www.hhs.gov/about/news/2020/12/11/trump-administration-purchases-additional-100-million-doses-covid-19-investigational-vaccine-moderna.html"
      }}>{`purchased 100 million doses`}</a>{` and distributed 11. Supply is not the problem.`}</p>
    <p><em parentName="p">{`Fairness`}</em>{` is a problem. 100 million is a 3rd of the population. Distribute without priority and you could leave the people who need it most.`}</p>
    <p>{`But if you can vaccinate fast enough, fairness doesn't matter. If everyone gets vaccinated in 4 weeks, does it matter that grandma waited 2 weeks and you waited 2 days? Nah.`}</p>
    <p>{`🤔`}</p>
    <h2 {...{
      "id": "how-do-you-design-the-fastest-queue"
    }}>{`How do you design the fastest queue?`}</h2>
    <p>{`Queue performance depends on λ – arrival rate – and μ – processing speed. Arrival rate fills up your queue, processing drains it.`}</p>
    <p>{`We know the queue is full. That leaves processing speed.`}</p>
    <p>{`I recently lucked into getting vaccinated. It took 60 seconds. Doctor says hi, walks you to a chair, asks about allergies, gives you a pamphlet, delivers the shot, walks you out.`}</p>
    <p>{`60 seconds of their time.`}</p>
    <p>{`You spend many minutes getting there, 5 minutes in the waiting room, 15 minutes afterwards waiting for any allergic reactions. We can ignore `}<em parentName="p">{`your`}</em>{` time for now.`}</p>
    <p>{`What's the fastest you can get everyone vaccinated?`}</p>
    <h2 {...{
      "id": "a-single-fifo-queue-with-1-server"
    }}>{`A single FIFO queue with 1 server`}</h2>
    <p>{`Let's try a `}{`[first-in-first-out queue]`}{`(`}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/FIFO_(computing_and_electronics)"
      }}>{`https://en.wikipedia.org/wiki/FIFO`}{`_`}{`(computing_and_electronics)`}</a>{`. First come first served. Like a taco truck.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/21412a0809d3daa437b8b6e257e14ee9/9f9a4/FIFO-queue3hjhbf.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "41.70403587443946%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'167\\'%20viewBox=\\'0%200%20400%20167\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M391%209l-2%207-3%202c-2%200-3-1-3-6%200-8-2-8-2%200%200%205%200%205%203%207%202%201%203%202%203%204%200%204%202%208%204%2010%204%202%205%201%201-3-3-3-4-9-1-12l4-11c0-3-2-2-4%202m-74%2036l-26%203c-23%201-44%204-45%207l2%201c2-1%202-1%201%203-4%2017-5%2022-4%2024s7%203%2014%200c13-4%2029-6%2043-6%2017%200%2015%201%2019-13%205-18%204-22-4-19m2%203l-23%202c-17%200-27%201-40%204-5%201-4%200-7%2011-4%2021-5%2020%2012%2016%2021-5%2028-6%2041-6l14-1%207-26c0-2-1-2-4%200m-93%203l-35%202-34%201c-2%200-2%201-2%206l-2%2014c-5%2017-3%2019%2015%2014%2011-2%2012-3%2038-3l25-1c-1-3%200-16%201-21%202-14%201-16-6-12m-99%200c0%201-8%202-18%202-33%203-39%204-40%203s-3%201-3%205c-2%206-3%2024-2%2027s6%203%2015%200a178%20178%200%200162-3c2-1%201-4-2-12l-5-13-2-8-5-1m100%202l-37%202h-33v5l-2%2012-2%2013c0%205%200%205%2016%201%2011-2%2013-2%2036-2h24v-9l1-15c1-9%201-9-3-7m-99%200a420%20420%200%2001-58%206c-3%201-3%202-5%2020%200%2011%200%2011%2012%208%2017-4%2037-5%2056-3h8l-2-5-3-8-3-8c-2-9-3-11-5-10M46%2080l-6%201h6c8%200%2010%200%208%204l-1%203-1-3c-1-6-15-1-23%209-6%208-17%2036-14%2038%201%201%201%201%201-1%200-4%206-23%2010-29%205-10%2020-21%2024-17%201%200-1%205-5%2010-3%204-4%206-2%206l1-1%206-8c7-8%207-11%203-12l-2-1-5%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/21412a0809d3daa437b8b6e257e14ee9/ca0a1/FIFO-queue3hjhbf.webp 223w", "/static/21412a0809d3daa437b8b6e257e14ee9/75680/FIFO-queue3hjhbf.webp 445w", "/static/21412a0809d3daa437b8b6e257e14ee9/8d1ba/FIFO-queue3hjhbf.webp 890w", "/static/21412a0809d3daa437b8b6e257e14ee9/3838e/FIFO-queue3hjhbf.webp 1335w", "/static/21412a0809d3daa437b8b6e257e14ee9/9fbc2/FIFO-queue3hjhbf.webp 1560w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/21412a0809d3daa437b8b6e257e14ee9/e92b6/FIFO-queue3hjhbf.png 223w", "/static/21412a0809d3daa437b8b6e257e14ee9/e66bf/FIFO-queue3hjhbf.png 445w", "/static/21412a0809d3daa437b8b6e257e14ee9/4ef49/FIFO-queue3hjhbf.png 890w", "/static/21412a0809d3daa437b8b6e257e14ee9/4e814/FIFO-queue3hjhbf.png 1335w", "/static/21412a0809d3daa437b8b6e257e14ee9/9f9a4/FIFO-queue3hjhbf.png 1560w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/21412a0809d3daa437b8b6e257e14ee9/4ef49/FIFO-queue3hjhbf.png",
                "alt": "FIFO queue",
                "title": "FIFO queue",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`FIFO queue`}</figcaption>{`
  `}</figure></p>
    <p>{`You've got 1 doctor working their butt off. They need no sleep and no rest. Everyone shows up on time.`}</p>
    <p>{`At 60 seconds per patient, you'll need 328,000,000 minutes. 624 years.`}</p>
    <h2 {...{
      "id": "a-fifo-queue-with-multiple-servers"
    }}>{`A FIFO queue with multiple servers`}</h2>
    <p>{`You'll need more than 1 vaccination site. How many? Is FIFO the best approach?`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/9c727712ff04ba1b2edffb92d8d7a978/f349c/FIFO-with-N-servers23b678.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "33.18385650224215%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'132\\'%20viewBox=\\'0%200%20400%20132\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M248%2036l-17%202c-15%200-37%203-38%205v1c2%200%202%200%200%2011-3%2013-2%2014%2014%2010%2013-4%2025-5%2034-4%208%200%208%201%2011-13%203-13%203-15-4-12m1%202l-15%201c-13%200-36%203-37%205-2%203-4%2020-3%2021l13-2c10-3%2015-3%2026-4%2016%200%2015%201%2018-14%202-8%202-9-2-7m-72%202l-28%202c-27%200-28%200-27%205l-2%2012c-3%2014-3%2014%2015%2010%2010-2%2013-2%2029-2l17%201V57l2-15c0-3-1-4-6-2m-77%200l-40%204-7%201-1%203-1%2012c-1%2012%200%2012%2011%2010%2014-3%2029-4%2039-3%2012%201%2012%201%209-6l-4-12c-2-8-4-10-6-9m76%202l-27%201-25%201-1%205-1%2010c-4%2012-3%2012%2011%209%207-2%2011-2%2028-2h19V41l-4%201m-81%201a560%20560%200%2001-41%204c-1%202-3%2021-1%2022l11-1c8-2%2013-2%2028-2l18-1-2-6-4-10c-2-7-1-7-9-6\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9c727712ff04ba1b2edffb92d8d7a978/ca0a1/FIFO-with-N-servers23b678.webp 223w", "/static/9c727712ff04ba1b2edffb92d8d7a978/75680/FIFO-with-N-servers23b678.webp 445w", "/static/9c727712ff04ba1b2edffb92d8d7a978/8d1ba/FIFO-with-N-servers23b678.webp 890w", "/static/9c727712ff04ba1b2edffb92d8d7a978/3838e/FIFO-with-N-servers23b678.webp 1335w", "/static/9c727712ff04ba1b2edffb92d8d7a978/e11e5/FIFO-with-N-servers23b678.webp 1780w", "/static/9c727712ff04ba1b2edffb92d8d7a978/741df/FIFO-with-N-servers23b678.webp 2002w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9c727712ff04ba1b2edffb92d8d7a978/e92b6/FIFO-with-N-servers23b678.png 223w", "/static/9c727712ff04ba1b2edffb92d8d7a978/e66bf/FIFO-with-N-servers23b678.png 445w", "/static/9c727712ff04ba1b2edffb92d8d7a978/4ef49/FIFO-with-N-servers23b678.png 890w", "/static/9c727712ff04ba1b2edffb92d8d7a978/4e814/FIFO-with-N-servers23b678.png 1335w", "/static/9c727712ff04ba1b2edffb92d8d7a978/701e9/FIFO-with-N-servers23b678.png 1780w", "/static/9c727712ff04ba1b2edffb92d8d7a978/f349c/FIFO-with-N-servers23b678.png 2002w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/9c727712ff04ba1b2edffb92d8d7a978/4ef49/FIFO-with-N-servers23b678.png",
                "alt": "FIFO with N servers",
                "title": "FIFO with N servers",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`FIFO with N servers`}</figcaption>{`
  `}</figure></p>
    <p>{`If you had 328,000,000 doctors, the queue falls to zero. Everyone vaccinates themselves and you're done in 1 minute. The problem becomes delivering the medicine.`}</p>
    <p>{`USPS is good at logistics, but do you trust people to do it themselves? And to `}<a parentName="p" {...{
        "href": "https://www.reuters.com/article/health-coronavirus-vaccines-distribution/why-pfizers-ultra-cold-covid-19-vaccine-will-not-be-at-the-local-pharmacy-any-time-soon-idUSKBN27P2VP"
      }}>{`keep the medicine at cryogenic temperatures`}</a>{`?`}</p>
    <p>{`😅`}</p>
    <p>{`Maybe not.`}</p>
    <p>{`To vaccinate everyone in a month, you'll need:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/ac36405dbdac4ce18a330b128f5e1a66/8f0f9/Vaccines-per-minutea81262.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "41.70403587443946%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'167\\'%20viewBox=\\'0%200%20400%20167\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M34%2011c-2%201-3%204-1%204l3-2%203-1c3%201%202%204-2%208-7%207-6%2011%203%2010%204-1%204-3-1-3-6%200-6%200%200-5%207-8%204-17-5-11M9%2011c-2%202-2%205%200%206l2-2c0-2%202-2%205-1%204%201%204%203-1%204-3%202-3%204%200%204s6%202%206%204l-9%203c-3%200-4%201-1%202%208%202%2016-5%2010-9-2-2-2-2-1-3%204-5-6-12-11-8m48%202v3l-1%204c-3%202-2%205%200%208%203%203%206%203%208%200%202-2%202-3%201-7-1-1%200-2%201-2%203-1%202-4-1-6-4-3-7-3-8%200m147%202c-6%206-5%207%204%207%206%200%208-1%208-6%200-6-6-7-12-1M86%2013l-1%203-1%202c-2%202-3%205-1%206%201%202%2011%203%2014%201%204-2%201-9-5-12h-6m188%203l-4%205c0%203%2010%204%2014%201%202-2%201-7-1-8-4-3-5-2-9%202m-165-2l-3%205c-4%206-3%207%206%207%2010-1%2012-8%204-12h-7m21%202l-5%204c-4%203%205%206%2014%204%206-1%204-7-3-10-4-1-3-1-6%202m36%201l-3%204c-3%203-1%204%206%204%209%200%2011-5%204-9-4-3-6-2-7%201m18%200l-3%205c0%202%205%203%2010%202%208-1%209-4%202-8-5-3-5-3-9%201m76%200l-2%203-1-2c-1-4-2-3-5%201l-1%204%201-4c0-4-2-6-3-2-2%203-1%209%200%209%202%200%205-3%205-5%200-1%201-1%202%201%202%201%202%201%204-1%201-2%202-2%203-1%201%202%204%203%204%201l-1-1-1-3c-1-4-3-4-5%200m23%2023l-1%207c0%203%200%203-1%202s-2-2-1-3c0-1-3-3-4-1-1%201%205%2011%208%2013%202%201%207-2%209-7%202-4-1-3-4%201l-4%204c-1%200-1-7%201-15%200-5-1-5-3-1m-119%208l-75%201a336%20336%200%2000-82%204c6-2%2058-3%2083-2%2034%201%2050%201%2071-1a158%20158%200%200149%200c1-1%200-1-2-2l-5-1c-4-1-23-1-39%201M35%2067c-5%207-3%2011%204%209%204%200%204%200%204%203%201%204%203%204%203%200l1-5v-4c-1-2-2-2-3%201-2%202-3%203-6%203-4%200-4-1%200-5l1-5-4%203m75%200l-1%203-3%203c-5%203-5%206%200%206s15-3%2016-5c0-1-1-3-4-5-4-3-6-4-8-2m26%201l-5%204c-5%203%200%206%208%205%206%200%208-2%208-5%200-4-9-8-11-4m-51%200c-1%202-1%2012%201%2013%204%203%2011%200%208-3-1-2-1-5%201-5%204%200%204-3%201-4-4-2-9-2-11-1m225%209l-3%205c0%203%200%203%205%203%209%200%2014-6%209-10-4-3-7-2-11%202m-32-2c-4%201-5%2013-1%2015s7%201%207-2l-1-3c-2-2-1-2%202-4%205-3%204-4-3-6h-4m17%202l-4%205c-3%203-2%204%203%204%207-1%2010-3%2010-6%201-4%200-6-5-6-2%200-3%201-4%203M82%20106l-1%204c0%203%200%204-2%205-4%201-4%202%200%208%204%208%208%207%2018-2%204-4%202-5-3-1-6%206-8%205-4-4v-10c-2%201-4%2011-4%2016s-1%206-4%203v-7c3-9%202-15%200-12m-42%2036c-1%201%200%202%201%204%201%204%203%203%202-1-1-2-1-2%203-2%206%200%205%206-2%208l-4%202c-1%201-1%201%202%201%203-1%203%200%203%202s1%203%202%203l1-2c-1-3%200-5%203-6s4-3%201-3v-2c2-3%200-5-7-5l-5%201m43%202l-2%208v6h3c5%200%206-3%204-6-3-2-2-3%201-4%205-2%203-7-2-7-2%200-3%201-4%203m20%200l-2%204-1%205c-2%205%202%209%207%207%202%200%202-5%201-7-3-2-3-3%202-4s6-5%201-6l-4-1-4%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/ac36405dbdac4ce18a330b128f5e1a66/ca0a1/Vaccines-per-minutea81262.webp 223w", "/static/ac36405dbdac4ce18a330b128f5e1a66/75680/Vaccines-per-minutea81262.webp 445w", "/static/ac36405dbdac4ce18a330b128f5e1a66/8d1ba/Vaccines-per-minutea81262.webp 890w", "/static/ac36405dbdac4ce18a330b128f5e1a66/621dd/Vaccines-per-minutea81262.webp 1045w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/ac36405dbdac4ce18a330b128f5e1a66/e92b6/Vaccines-per-minutea81262.png 223w", "/static/ac36405dbdac4ce18a330b128f5e1a66/e66bf/Vaccines-per-minutea81262.png 445w", "/static/ac36405dbdac4ce18a330b128f5e1a66/4ef49/Vaccines-per-minutea81262.png 890w", "/static/ac36405dbdac4ce18a330b128f5e1a66/8f0f9/Vaccines-per-minutea81262.png 1045w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/ac36405dbdac4ce18a330b128f5e1a66/4ef49/Vaccines-per-minutea81262.png",
                "alt": "Vaccines per minute",
                "title": "Vaccines per minute",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Vaccines per minute`}</figcaption>{`
  `}</figure></p>
    <p>{`To achieve a vaccination rate of 7488/min, you'll need 7488 vaccination sites that never rest. We don't have that many and they `}<em parentName="p">{`do`}</em>{` need rest.`}</p>
    <h3 {...{
      "id": "more-realistic-behavior"
    }}>{`More realistic behavior`}</h3>
    <p>{`Let's assume a typical clinic stays open for 8 hours. Hospitals have shifts and operate 24/7, but that averages out.`}</p>
    <p>{`Or as physicists say: `}<em parentName="p">{`"A cow is roughly a sphere"`}</em></p>
    <p>{`At 8 hours/day and working full-time on vaccinations, your 7488 sites take`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "753px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/4dc759070d7f50c2b36daad7db1d9d88/17a7a/Days-to-vaccinate-everyoneaf72ag.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "46.18834080717489%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'184\\'%20viewBox=\\'0%200%20400%20184\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M57%206c-2%202%200%205%202%203%201-2%206-1%206%201l-4%203c-4%202-4%204-1%204%204%200%208%203%206%205-2%203-12%206-13%204s-3%200-3%202c2%205%2020-2%2020-7l-3-4c-2-2-2-2%200-5V7c-3-2-8-3-10-1m21%202c-2%201-2%202-1%203h3c1-2%202-2%204%200%204%202%204%202-3%207-10%206-9%209%202%209%209-1%209-3%201-3h-6l4-3c9-6%2010-8%205-12-4-3-7-4-9-1m24-1l-1%203-2%206c-5%206%200%2016%208%2015%202-1%203-6%201-11l-2-4h4l5-1c2-3-11-11-13-8m59%201c0%202%203%205%205%205%201-1%203%200%205%201l2%203-6-1c-7%200-10%201-10%205%200%203%204%203%2013%200%208-2%208-5-2-12-5-3-6-4-7-1m27%200c-2%203-1%208%201%208l1-3c0-3%200-4%202-4%203%200%207%204%207%206-1%203-6%205-7%203-1-1-8-1-11%201-4%204%208%205%2016%202%204-1%205-2%205-5%200-7-11-13-14-8m35%200l-1%202-3%203c-7%204%200%2012%2011%2012%206%200%207%200%207-6%200-7-10-15-14-11m56%201l-7%205c0%202%203%201%207-1%206-5%2013-3%2011%203-1%204-5%206-9%205-3%200-3%200-2-2%203-2%202-4-1-4-3%201-4%203-4%205%200%203%203%204%208%204%206%201%2012-4%2012-10s-9-9-15-5M137%209v4c1%201%200%202-2%203l-3%204c0%202%201%203%207%202%207%200%2013-3%2013-7-1-4-14-9-15-6m88%202l1%203c1%201%201%201-3%201-9%201-2%207%207%207%204%200%204%200%204-3%200-2-2-7-4-8h-5m27%201l-4%202c-4%200-4%206%200%208%206%205%2017%201%2017-6%200-5-11-8-13-4M117%2032l-2%203H65l-48-1c2-1%203-3%201-3l-8%203c-5%202-5%202-3%204h8c15-1%2088%200%20124%202%2065%204%20107%203%20178-1%2047-4%2066-4%2072%200%204%202%206%201%203-2-4-5-25-6-81-1l-73%203c-39%200-42%200-33-3%203-2%204-3%202-4-1-1-8%202-11%205l-3%202h-18l-57-4%203-3c2-2%203-3%200-3l-4%203M83%2057c-7%2010-8%2013-2%2013h4l-1%205c0%208%203%208%204-1%200-3%201-5%202-5%202%200%201-3-1-3v-1l2-3c0-2-3-1-5%202l-4%202%203-5c5-5%206-9%204-9l-6%205m-26-3c-8%201-10%202-7%205%202%202%206%202%205%200-1-1%204-2%209-2h3l-3%204c-3%203-4%204-9%204-7%201-9%202-8%205h2l5-1c4%200%204%200%203%202l1%208c1%202%202%200%202-3%200-5%202-8%204-7%203%200%205-2%203-4-2-1-2-1%201-5%205-6%203-8-11-6m59%200c-4%203-6%2019-3%2024%202%204%206%206%208%205%204-2%201-14-4-14v-1l5-1%205-1c1-2%201-2-4-7s-6-6-7-5m125%202l-2%205-2%204c-2%203-3%205%200%208%202%203%2010%204%2011%200%201-1-1-6-3-7-1-1%200-1%203-2%207-1%208-5%203-9-4-3-8-3-10%201m-41%200l-1%203-3%204c-3%201-5%205-4%207%201%203%2016-2%2018-5%203-6-5-13-10-9M97%2058l-1%206-1%205c-2%203%201%2010%203%2012%206%203%209-2%206-10-2-3-2-3%200-4%206-2%206-4%201-8-6-3-7-4-8-1m79%203c-5%205-7%208-5%2011%202%204%208%204%2013%200%203-3%202-4-5-4l-5-1%205-5c3-2%204-4%204-5-1-1-3%201-7%204m-28%2040l-2%207c-1%2015-2%2016-2%2013l-1-4c1-1%201-2-1-3-4%200-5%200-3%204l2%208c3%2015%2011%2013%2020-4%204-8%201-8-4%200-1%202-1-3%202-13%201-6%201-9-1-9-2%201-5%2017-5%2024%200%208-5%2012-8%206v-5c2%200%202%201%205-21v-4l-2%201m40%2048l-2%207c0%205-5%2010-6%207%200-2%200-2-9%200-8%203-9%204-4%206%204%201%209%201%2014-2%203-1%205-2%207-1%203%200%204-1%202-3-1-1-2-2-1-3l1-14-2%203m-88%200l-4%203c1%202%201%202%207%202%203%200%204%200%203%204%200%203%200%204%203%207%206%206%200%209-8%204-4-3-5-1%200%202%204%204%2010%204%2013%201s3-4-1-8-5-6-2-9c1-2%201-4-1-4h-4c-2%200-2%200%200-1l2-2c2-2-3-1-8%201m130%207c-2%202%200%203%206%204l5%201-5%205c-5%204-10%2012-8%2012l4-4%207-7c3-2%207-8%205-8l-13-4-1%201m25%201c-2%202-3%208-1%2010l6%202c7%202%206%203-6%204-5%201-1%203%205%203%2012%200%2013-6%202-9-5-2-5-2-5-5s0-3%202-2c2%200%203-2%201-4-2-1-2-1-4%201m-42%202c0%203-2%204-6%204h-4l3-1c3-1%204-4%201-4-7%200-12%204-9%206%202%202%2013%203%2014%201h3c1%202%204%204%206%204s1-3-2-4c-2-1-3-2-3-5s-3-4-3-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4dc759070d7f50c2b36daad7db1d9d88/ca0a1/Days-to-vaccinate-everyoneaf72ag.webp 223w", "/static/4dc759070d7f50c2b36daad7db1d9d88/75680/Days-to-vaccinate-everyoneaf72ag.webp 445w", "/static/4dc759070d7f50c2b36daad7db1d9d88/67c6f/Days-to-vaccinate-everyoneaf72ag.webp 753w"],
                "sizes": "(max-width: 753px) 100vw, 753px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4dc759070d7f50c2b36daad7db1d9d88/e92b6/Days-to-vaccinate-everyoneaf72ag.png 223w", "/static/4dc759070d7f50c2b36daad7db1d9d88/e66bf/Days-to-vaccinate-everyoneaf72ag.png 445w", "/static/4dc759070d7f50c2b36daad7db1d9d88/17a7a/Days-to-vaccinate-everyoneaf72ag.png 753w"],
                "sizes": "(max-width: 753px) 100vw, 753px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/4dc759070d7f50c2b36daad7db1d9d88/17a7a/Days-to-vaccinate-everyoneaf72ag.png",
                "alt": "Days to vaccinate everyone",
                "title": "Days to vaccinate everyone",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Days to vaccinate everyone`}</figcaption>{`
  `}</figure></p>
    <p>{`91 days. That's 3 months. Is 3 months long enough to worry about priority and equitable outcome? 🤔`}</p>
    <h2 {...{
      "id": "what-about-people-who-say-no"
    }}>{`What about people who say no?`}</h2>
    <p>{`You've got 7488 doctors working 8 hours per day, vaccinating 1 person per minute. You don't need 7488 clinics for this, doctors can work side by side.`}</p>
    <p>{`And when `}<a parentName="p" {...{
        "href": "https://www.boropark24.com/news/nypd-waits-for-vaccine-while-30-of-fdny-reject-the-shots"
      }}>{`30% say no`}</a>{` that destroys your FIFO queue.`}</p>
    <p>{`FIFO holds spots. You put everyone in the queue. When it's their turn you reach out.`}</p>
    <pre><code parentName="pre" {...{}}>{`1. Hey your turn
2. Nah
3. move on to next person
`}</code></pre>
    <p>{`That takes time too! It's processing before processing. Technically a queue feeding into the vaccination queue.`}</p>
    <h3 {...{
      "id": "absolute-fairness"
    }}>{`Absolute fairness`}</h3>
    <p>{`To ensure absolute fairness, you have to `}<em parentName="p">{`wait for a response`}</em>{`. Even if everyone is ridiculously fast to reply – 5 minutes – that increases processing time to 6 minutes.`}</p>
    <p>{`In the best case (everyone says Yes) that increases your time from 91 days to `}<strong parentName="p">{`547 days`}</strong>{`. Just to stay fair. 🤯`}</p>
    <p>{`And we know people don't reply that fast.`}</p>
    <p>{`Every No adds another 5min wait. You can model average wait time as a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Bernoulli_trial"
      }}>{`Bernoulli trial`}</a>{` and that's where my math taps out.`}</p>
    <h3 {...{
      "id": "lossy-fairness"
    }}>{`Lossy fairness`}</h3>
    <p>{`Lossy fairness means you `}<em parentName="p">{`don't wait for a response`}</em>{`. Offer vaccine, move on to the next person.`}</p>
    <p>{`You go as fast as you can, prioritize the needy, and replies don't slow you down.`}</p>
    <h2 {...{
      "id": "priority-queue-feeding-into-fifo"
    }}>{`Priority queue feeding into FIFO`}</h2>
    <p>{`Intuitively, a lossy fairness approach works best. You make vaccines available based on priority and vaccinate based on who says Yes first.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/5049e810b85578768f692eacfa19ab89/437e0/Priority-queue-feeding-a-FIFO531ga8.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "40.35874439461883%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'162\\'%20viewBox=\\'0%200%20400%20162\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M255%2035l-19%202c-16%201-35%204-35%205l2%201%201%202-3%209c-2%2012-1%2013%2015%208%207-2%2013-2%2024-3l15-1c1-2%206-21%205-23h-5m1%202l-14%201c-11%200-26%202-33%204-3%201-4%201-5%2010-2%206-2%2010-1%2011l13-2c9-3%2014-3%2024-4%2015%200%2014%201%2017-11%203-11%203-11-1-9m-70%202l-26%202c-25%200-27%200-26%204l-2%2012c-3%2013-3%2014%2014%2010%208-2%2012-2%2027-2h17v-7l1-13c1-8%201-8-5-6m-73%200l-40%204c-6%201-6%200-7%2016-1%209%200%2011%207%209a154%20154%200%200150-3c2%200%201-4-1-8l-3-10c-2-6-4-9-6-8m73%202l-26%201-24%201-1%206-2%2011c-2%208-1%209%2010%206%207-2%2011-2%2027-2h19V40l-3%201m-87%202l-23%202c-8%200-8%200-9%2013v9h2l11-2%2025-1%2018-1-2-5-4-10-3-7-15%202M60%2077l-13%201c-13%201-16%201-14%203l1%208%201%206h3l13-2%2012-2c4%200%205%200%206-7%200-6-1-8-5-8l-4%201m-7%202l-11%201h-7v7l1%207%204-1%2026-5%201-6v-4h-5l-9%201m-31%203C8%2089%200%20110%206%20128l4%209c2%203%202%202-1-4-9-19%200-46%2016-50l4-2c0-1-1-1-7%201m28%2017c-15%203-15%203-15%2010s1%208%2011%206l12-2c8%200%208%200%208-7%200-6-1-9-4-9l-12%202m5%201l-13%202-5%202-1%205v5h5l14-2%2010-2-1-5c0-7%200-7-9-5m-4%2021l-10%201c-4%200-8%202-4%202l15-2c17-3%2019-2%2017%206-1%202-1%203-9%203l-15%202c-10%202-7%203%204%201l13-2c7%200%207%200%208-3%202-4%201-9-1-9-2-1-12-1-18%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5049e810b85578768f692eacfa19ab89/ca0a1/Priority-queue-feeding-a-FIFO531ga8.webp 223w", "/static/5049e810b85578768f692eacfa19ab89/75680/Priority-queue-feeding-a-FIFO531ga8.webp 445w", "/static/5049e810b85578768f692eacfa19ab89/8d1ba/Priority-queue-feeding-a-FIFO531ga8.webp 890w", "/static/5049e810b85578768f692eacfa19ab89/3838e/Priority-queue-feeding-a-FIFO531ga8.webp 1335w", "/static/5049e810b85578768f692eacfa19ab89/e11e5/Priority-queue-feeding-a-FIFO531ga8.webp 1780w", "/static/5049e810b85578768f692eacfa19ab89/72e28/Priority-queue-feeding-a-FIFO531ga8.webp 2094w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5049e810b85578768f692eacfa19ab89/e92b6/Priority-queue-feeding-a-FIFO531ga8.png 223w", "/static/5049e810b85578768f692eacfa19ab89/e66bf/Priority-queue-feeding-a-FIFO531ga8.png 445w", "/static/5049e810b85578768f692eacfa19ab89/4ef49/Priority-queue-feeding-a-FIFO531ga8.png 890w", "/static/5049e810b85578768f692eacfa19ab89/4e814/Priority-queue-feeding-a-FIFO531ga8.png 1335w", "/static/5049e810b85578768f692eacfa19ab89/701e9/Priority-queue-feeding-a-FIFO531ga8.png 1780w", "/static/5049e810b85578768f692eacfa19ab89/437e0/Priority-queue-feeding-a-FIFO531ga8.png 2094w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/5049e810b85578768f692eacfa19ab89/4ef49/Priority-queue-feeding-a-FIFO531ga8.png",
                "alt": "Priority queue feeding a FIFO",
                "title": "Priority queue feeding a FIFO",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Priority queue feeding a FIFO`}</figcaption>{`
  `}</figure></p>
    <p>{`Rejections don't affect processing time. Fairness guaranteed by probability. It takes time to go down the prioritized list and send emails. You can do it in batches.`}</p>
    <p>{`That's what Israel did. `}<em parentName="p">{`"If you're 85 and older, come and get it"`}</em>{`. When batch runs out, say `}<em parentName="p">{`"If you're 80 and older, come and get it"`}</em>{`.`}</p>
    <p>{`It worked.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`Shots. In. Arms. Forget fairness and priorities, dole that shit out. Make it fly.`}</p>
    <p>{`And we kinda derived `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Little%27s_law"
      }}>{`Little's Law`}</a>{`. Queue length depends on arrival rate and time spent in the system. Nothing else.`}</p>
    <p>{`And we had nerdy fun ✌️`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: this is all back-of-the-envelope math, I am not a licensed statistician and it took me 4 tries to pass the damn queuing theory exam. Also I don't have all the data`}</p>
    <p>{`[sparkjoy|queue-theory-vaccines]`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      